import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Send,
  LayoutDashboard,
  IdCard,
  HeartPulse,
  FlaskConical,
  Pill,
  FileText,
  FolderOpen,
  Clock,
  Play,
  Award,
  X,
} from "@/components/icons";
import { toast } from "sonner";
import api, { formatErr } from "../../lib/api";
import { useAuth } from "../../lib/auth";

import OverviewTab from "./patient/OverviewTab";
import FullInfoTab from "./patient/FullInfoTab";
import { VitalsTab, LabTab, MedicationTab, NotesTab } from "./patient/ClinicalTabs";
import FilesTab from "./patient/FilesTab";
import HistoryTab from "./patient/HistoryTab";
import { VitalsModal, NoteModal } from "./patient/modals";
import PatientHeaderCard from "./patient/PatientHeaderCard";
import PrescriptionModal from "../../components/PrescriptionModal";
import ReferralModal from "../../components/ReferralModal";
import AppleModal from "../../components/AppleModal";
import { sanitizePatient } from "./patient/common";

const TABS = [
  { id: "Overview", label: "Overview", icon: LayoutDashboard },
  { id: "Información completa", label: "Información completa", icon: IdCard },
  { id: "Vitales", label: "Vitales", icon: HeartPulse },
  { id: "Laboratorio", label: "Laboratorio", icon: FlaskConical },
  { id: "Medicación", label: "Medicación", icon: Pill },
  { id: "Notas / Docs", label: "Notas / Docs", icon: FileText },
  { id: "Archivos", label: "Archivos", icon: FolderOpen },
  { id: "Historial", label: "Historial", icon: Clock },
];

export default function PatientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [, setUploadingPhoto] = useState(false);
  const [vitalsOpen, setVitalsOpen] = useState(false);
  const [editingVitals, setEditingVitals] = useState(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [rxOpen, setRxOpen] = useState(false);
  const [editingRx, setEditingRx] = useState(null);
  const [refOpen, setRefOpen] = useState(false);
  const [certOpen, setCertOpen] = useState(false);
  const [startingConsult, setStartingConsult] = useState(false);
  const fileRef = useRef();
  const navigate = useNavigate();

  const load = () => api.get(`/patients/${id}`).then((r) => setData(r.data));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const actionsRef = useRef({});
  useEffect(() => {
    const onAction = (e) => {
      if (e.detail?.patientId !== id) return;
      actionsRef.current[e.detail.action]?.();
    };
    window.addEventListener("consu:patient-action", onAction);
    return () => window.removeEventListener("consu:patient-action", onAction);
  }, [id]);

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 800_000) {
      toast.error("La imagen es muy grande (máx 800KB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setUploadingPhoto(true);
      try {
        const p = data.patient;
        await api.put(`/patients/${id}`, {
          ...sanitizePatient(p),
          photo_url: reader.result,
        });
        toast.success("Foto actualizada");
        load();
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail));
      } finally {
        setUploadingPhoto(false);
      }
    };
    reader.readAsDataURL(f);
  };




  const latestDx = useMemo(() => {
    if (!data?.diagnoses?.length) return null;
    return [...data.diagnoses].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    )[0];
  }, [data]);

  const nextAppt = useMemo(() => {
    if (!data?.appointments?.length) return null;
    const future = data.appointments
      .filter((a) => new Date(a.date) >= new Date() && a.status !== "cancelled")
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    return future[0] || null;
  }, [data]);

  const latestRx = useMemo(() => {
    if (!data?.prescriptions?.length) return null;
    return [...data.prescriptions].sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    )[0];
  }, [data]);

  if (!data) return (
    <div className="flex-1 flex items-center justify-center text-[13px] text-black/50">
      Cargando…
    </div>
  );

  const { patient, diagnoses, appointments, prescriptions } = data;
  const back = user?.role === "secretary" ? "/secretaria/pacientes" : "/doctor/pacientes";
  const v = latestDx?.vitals || {};
  const isDoctor = user?.role === "doctor";

  // Iniciar consulta: si hay una cita in_progress o próxima, la abre; si no, crea walk-in
  const startConsult = async () => {
    const inProgress = appointments.find((a) => a.status === "in_progress");
    if (inProgress) {
      navigate(`/doctor/consulta/${inProgress.id}`);
      return;
    }
    const upcoming = appointments
      .filter((a) => new Date(a.date) >= new Date() && a.status !== "completed" && a.status !== "cancelled")
      .sort((a, b) => new Date(a.date) - new Date(b.date))[0];
    if (upcoming) {
      try {
        await api.put(`/appointments/${upcoming.id}`, {
          patient_id: upcoming.patient_id,
          office_id: upcoming.office_id,
          date: upcoming.date,
          duration_min: upcoming.duration_min,
          reason: upcoming.reason,
          status: "in_progress",
        });
      } catch { /* navigate anyway */ }
      navigate(`/doctor/consulta/${upcoming.id}`);
      return;
    }
    // No hay cita — crea walk-in ahora
    setStartingConsult(true);
    try {
      const now = new Date().toISOString();
      const { data: appt } = await api.post("/appointments", {
        patient_id: id,
        office_id: patient.office_id || null,
        date: now,
        duration_min: 30,
        reason: "Consulta walk-in",
        status: "in_progress",
      });
      navigate(`/doctor/consulta/${appt.id}`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "No pude iniciar la consulta");
    } finally {
      setStartingConsult(false);
    }
  };

  actionsRef.current = {
    consulta: () => !startingConsult && startConsult(),
    receta: () => setRxOpen(true),
    certificado: () => setCertOpen(true),
    referido: () => setRefOpen(true),
    vitales: () => { setEditingVitals(null); setVitalsOpen(true); },
    nota: () => { setEditingNote(null); setNoteOpen(true); },
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-3" data-testid="patient-detail">
      <input type="file" accept="image/*" hidden ref={fileRef} onChange={onPickPhoto} />

      {/* Ficha superior siempre visible con tabs integradas */}
      <PatientHeaderCard
        patient={patient}
        nextAppt={nextAppt}
        isDoctor={isDoctor}
        fileRef={fileRef}
        backTo={back}
        tabs={TABS}
        activeTab={tab}
        onChangeTab={setTab}
        onStartConsult={startConsult}
        startingConsult={startingConsult}
        onCreateRx={() => setRxOpen(true)}
        onCreateCert={() => setCertOpen(true)}
        onCreateReferral={() => setRefOpen(true)}
      />

      {/* Contenido de la pestaña seleccionada */}
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 no-scrollbar">
        {tab === "Overview" && (
        <OverviewTab
          patient={patient}
          diagnoses={diagnoses}
          appointments={appointments}
          prescriptions={prescriptions}
          latestDx={latestDx}
          latestRx={latestRx}
          nextAppt={nextAppt}
          v={v}
          fileRef={fileRef}
          isDoctor={isDoctor}
          onAddVitals={() => setVitalsOpen(true)}
          onAddNote={() => setNoteOpen(true)}
          onGoRecipes={() => setRxOpen(true)}
          onStartConsult={startConsult}
          startingConsult={startingConsult}
          onCreateRx={() => setRxOpen(true)}
          onCreateCert={() => setCertOpen(true)}
          onCreateReferral={() => setRefOpen(true)}
        />
      )}
      {tab === "Información completa" && (
        <FullInfoTab patient={patient} onSaved={load} />
      )}
      {tab === "Vitales" && (
        <VitalsTab
          diagnoses={diagnoses}
          isDoctor={isDoctor}
          onAdd={() => {
            setEditingVitals(null);
            setVitalsOpen(true);
          }}
          onEdit={(d) => {
            setEditingVitals(d);
            setVitalsOpen(true);
          }}
        />
      )}
      {tab === "Laboratorio" && <LabTab patientId={id} isDoctor={isDoctor} />}
      {tab === "Medicación" && (
        <MedicationTab
          patientId={id}
          prescriptions={prescriptions}
          isDoctor={isDoctor}
          onReload={load}
          onAdd={() => {
            setEditingRx(null);
            setRxOpen(true);
          }}
          onEdit={(rx) => {
            setEditingRx(rx);
            setRxOpen(true);
          }}
        />
      )}
      {tab === "Notas / Docs" && (
        <NotesTab
          diagnoses={diagnoses}
          isDoctor={isDoctor}
          onAdd={() => {
            setEditingNote(null);
            setNoteOpen(true);
          }}
          onEdit={(d) => {
            setEditingNote(d);
            setNoteOpen(true);
          }}
        />
      )}
      {tab === "Archivos" && <FilesTab patientId={id} isDoctor={isDoctor} />}
      {tab === "Historial" && (
        <HistoryTab
          patientId={id}
          diagnoses={diagnoses}
          appointments={appointments}
          prescriptions={prescriptions}
        />
      )}
      </div>

      {vitalsOpen && (
        <VitalsModal
          patientId={id}
          existing={editingVitals}
          onClose={() => {
            setVitalsOpen(false);
            setEditingVitals(null);
          }}
          onSaved={() => {
            setVitalsOpen(false);
            setEditingVitals(null);
            load();
          }}
        />
      )}
      {noteOpen && (
        <NoteModal
          patientId={id}
          existing={editingNote}
          onClose={() => {
            setNoteOpen(false);
            setEditingNote(null);
          }}
          onSaved={() => {
            setNoteOpen(false);
            setEditingNote(null);
            load();
          }}
        />
      )}
      {rxOpen && (
        <PrescriptionModal
          patientId={id}
          patientName={data?.patient?.full_name}
          existing={editingRx}
          onClose={() => {
            setRxOpen(false);
            setEditingRx(null);
          }}
          onSaved={() => {
            setRxOpen(false);
            setEditingRx(null);
            load();
          }}
        />
      )}
      {refOpen && (
        <ReferralModal
          patientId={id}
          patientName={data?.patient?.full_name}
          onClose={() => setRefOpen(false)}
          onSaved={() => {
            setRefOpen(false);
            toast.success("Referido enviado");
          }}
        />
      )}
      {certOpen && (
        <CertificateModal
          patientId={id}
          patientName={data?.patient?.full_name}
          defaultDx={latestDx?.diagnosis || ""}
          onClose={() => setCertOpen(false)}
          onSaved={(cert) => {
            setCertOpen(false);
            toast.success("Certificado emitido");
            navigate(`/doctor/certificados/${cert.id}`);
          }}
        />
      )}
    </div>
  );
}

// Tooltip que aparece a la derecha del icono en hover
function Tooltip({ label }) {
  return (
    <span
      className="pointer-events-none absolute left-full ml-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-black text-white text-[11px] font-semibold px-2 py-1 rounded-md whitespace-nowrap z-50 shadow-lg"
    >
      {label}
    </span>
  );
}

// Modal para crear certificado médico standalone (sin cita asociada)
function CertificateModal({ patientId, patientName, defaultDx, onClose, onSaved }) {
  const [dx, setDx] = useState(defaultDx || "");
  const [note, setNote] = useState(
    "Se recomienda reposo en casa por 3 días con fines de tratamiento y mejoría."
  );
  const [reason, setReason] = useState("a petición del interesado");
  const [place, setPlace] = useState("");
  const [dateIso, setDateIso] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!dx.trim()) {
      toast.error("El diagnóstico es obligatorio");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/certificates", {
        patient_id: patientId,
        diagnosis: dx.trim(),
        note: note.trim(),
        reason: reason.trim(),
        place: place.trim() || null,
        date_iso: dateIso,
      });
      onSaved?.(data);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "No pude emitir el certificado");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppleModal
      open
      onClose={saving ? undefined : onClose}
      icon={<Award weight="regular" className="w-8 h-8" strokeWidth={1.5} />}
      title="Nuevo certificado médico"
      subtitle={`Para ${patientName || "el paciente"}`}
      size="md"
      compact
      testid="cert-modal"
    >
      <form onSubmit={submit} className="space-y-3">
        <Field label="Diagnóstico *">
          <input
            value={dx}
            onChange={(e) => setDx(e.target.value)}
            placeholder="Ej: Bronquitis aguda"
            className="w-full h-10 rounded-xl border border-black/[0.08] bg-white px-3 text-[13px] text-black placeholder:text-black/30 focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/10 transition"
            data-testid="cert-dx"
          />
        </Field>

        <Field label="Indicaciones">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            className="w-full rounded-xl border border-black/[0.08] bg-white px-3 py-2 text-[13px] text-black placeholder:text-black/30 focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/10 transition resize-none"
            data-testid="cert-note"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Motivo">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full h-10 rounded-xl border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/10 transition"
              data-testid="cert-reason"
            />
          </Field>
          <Field label="Fecha">
            <input
              type="date"
              value={dateIso}
              onChange={(e) => setDateIso(e.target.value)}
              className="w-full h-10 rounded-xl border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/10 transition"
              data-testid="cert-date"
            />
          </Field>
        </div>

        <Field label="Lugar (opcional)">
          <input
            value={place}
            onChange={(e) => setPlace(e.target.value)}
            placeholder="Ej: Santo Domingo"
            className="w-full h-10 rounded-xl border border-black/[0.08] bg-white px-3 text-[13px] text-black placeholder:text-black/30 focus:outline-none focus:border-[#0071E3] focus:ring-4 focus:ring-[#0071E3]/10 transition"
            data-testid="cert-place"
          />
        </Field>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-black/[0.05]">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-9 px-3.5 rounded-lg bg-white border border-black/10 hover:bg-black/[0.04] text-[12.5px] font-semibold text-black transition disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="cert-submit"
            className="h-10 px-5 rounded-full bg-[#0071E3] text-white hover:bg-[#0077ED] text-[13px] font-semibold transition disabled:opacity-60"
          >
            {saving ? "Emitiendo…" : "Emitir certificado"}
          </button>
        </div>
      </form>
    </AppleModal>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45 mb-1.5">
        {label}
      </div>
      {children}
    </label>
  );
}
